import type { FC, ReactElement, ReactNode } from 'react'

import { Icon } from '@rentspree/component-v2/dist/icon'
import { useContext, createContext, useState } from 'react'
import { createPortal } from 'react-dom'

import { Div, RentSpreeSweetalertGlobalStyle } from 'components'
import { CloseButton, ModalWrapper, OverlayWrapper } from 'components/common/layouts/modal/style'

export type ModalContextType = {
  modal: boolean
  modalContent: ReactElement | null
  toggleModal: (content?: unknown) => void
  closeModal: () => void
}

type ModalProviderProps = {
  root?: string
  children: (args: Pick<ModalContextType, 'toggleModal' | 'closeModal'>) => ReactNode
} & IElement

type ModalProps = {
  root?: string
} & IElement

export const ModalContext = createContext<ModalContextType | null>(null)

const useModal = () => {
  const [modal, setModal] = useState(false)
  const [modalContent, setModalContent] = useState(null)

  const toggleModal = (content) => {
    setModal(!modal)

    if (content) {
      setModalContent(content)
    }
  }

  const closeModal = () => {
    setModal(false)
  }

  return {
    modal,
    modalContent,
    toggleModal,
    closeModal,
  }
}

const Modal: FC<ModalProps> = ({ root, ...props }) => {
  const { modalContent, closeModal, modal } = useContext(ModalContext) as ModalContextType

  if (!modal) {
    return null
  }

  return createPortal(
    <>
      <OverlayWrapper />
      <ModalWrapper {...props} role="dialog">
        <CloseButton id="modal-close-btn" onClick={() => closeModal()}>
          <Icon icon="close" size="md" />
        </CloseButton>
        <Div $position="relative">{modalContent}</Div>
      </ModalWrapper>
    </>,
    document.querySelector(root || '#app') as Element,
  )
}

export const ModalProvider: FC<ModalProviderProps> = ({ children, root, ...props }) => {
  const { modal, toggleModal, closeModal, modalContent } = useModal()

  return (
    <ModalContext.Provider value={{ modal, toggleModal, closeModal, modalContent }}>
      <RentSpreeSweetalertGlobalStyle />
      <Modal root={root || '#app'} {...props} />
      {children({ toggleModal, closeModal })}
    </ModalContext.Provider>
  )
}
